import { ApiErrorCode } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { createGlobalState, type RemovableRef, StorageSerializers, useSessionStorage } from '@vueuse/core';
import { computed, watch } from 'vue';

import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { prepareQueryInput } from '#domain/performance/kpi/prepare-query-input';
import { getHasStoresClosedForLessThanAMonth } from '#domain/performance/likeForLike/likeForLike.query';

function useLikeForLikeInfoBannerHidden(): RemovableRef<boolean> {
  return useSessionStorage('like4likeInfoBanner', true, {
    serializer: StorageSerializers.boolean
  });
}

function useLike4LikeErrorBannerHidden(): RemovableRef<boolean> {
  return useSessionStorage('like4likeErrorBanner', false, {
    serializer: StorageSerializers.boolean
  });
}

export const useLikeForLike = createGlobalState(() => {
  const {
    withTax: { isWithTax },
    taxonomy: { taxonomy },
    filters: { selectedFiltersKpiQuery },
    period: { selectedPeriod, comparisonPeriod },
    storePerimeter: { storePerimeter },
    myDataIncluded: { isMyDataIncluded }
  } = useKpisPerimeter();

  const isLikeForLikeInfoBannerHidden = useLikeForLikeInfoBannerHidden();
  const isLikeForLikeErrorBannerHidden = useLike4LikeErrorBannerHidden();

  const { data: hasStoresClosedForLessThanAMonth } = useQuery({
    queryKey: [
      getHasStoresClosedForLessThanAMonth.name,
      selectedPeriod,
      comparisonPeriod,
      selectedFiltersKpiQuery,
      isWithTax,
      taxonomy,
      storePerimeter,
      isMyDataIncluded
    ],
    queryFn: async () => {
      const input = prepareQueryInput({
        period: selectedPeriod,
        comparisonPeriod,
        filters: selectedFiltersKpiQuery,
        isWithTax,
        isMyDataIncluded,
        taxonomy,
        storePerimeter
      });

      return getHasStoresClosedForLessThanAMonth(input);
    },
    select: (result) => {
      if (result.status === 'error') {
        if (result.error !== ApiErrorCode.UNAUTHORIZED_DATA_ACCESS_PERMISSIONS) {
          throw new Error(result.error);
        }
        return undefined;
      }

      return result.data;
    }
  });

  const shouldDisplayLikeForLikeInfoBanner = computed(() => {
    if (storePerimeter.value === 'allStores' || !isLikeForLikeInfoBannerHidden.value) {
      return false;
    }

    return true;
  });

  const shouldDisplayLikeForLikeErrorBanner = computed(() => {
    if (storePerimeter.value === 'allStores' || !isLikeForLikeErrorBannerHidden.value) {
      return false;
    }

    return Boolean(
      hasStoresClosedForLessThanAMonth.value?.isEligible &&
      hasStoresClosedForLessThanAMonth.value?.hasStoresClosedForLessThanAMonth
    );
  });

  const dismissLikeForLikeInfoBanner = () => {
    isLikeForLikeInfoBannerHidden.value = false;
  };

  const dismissLikeForLikeErrorBanner = () => {
    isLikeForLikeErrorBannerHidden.value = false;
  };

  watch(storePerimeter, () => {
    isLikeForLikeInfoBannerHidden.value = true;
    isLikeForLikeErrorBannerHidden.value = true;
  });

  watch(hasStoresClosedForLessThanAMonth, () => {
    isLikeForLikeErrorBannerHidden.value = true;
  });

  return {
    shouldDisplayLikeForLikeInfoBanner,
    shouldDisplayLikeForLikeErrorBanner,
    dismissLikeForLikeInfoBanner,
    dismissLikeForLikeErrorBanner
  };
});
